// CPB JavaScript for AU site — Optimized + Zip-safe mount (no cloning)
// Goals:
// - Zip widget shows under price
// - File B can set inline-flex on price container without trapping Zip inline
// - No double-toggle accordion bug
// - No leaked timers
// - Handles async CPB/Klaviyo rendering with retries
// - More robust price parsing (keeps cents + commas)

window.PXUTheme = window.PXUTheme || {};
window.PXUTheme.contentCreator = window.PXUTheme.contentCreator || {};

(() => {
  'use strict';

  /* -------------------------------------------
   * Accordion module (delegated, no dbl-toggle)
   * ----------------------------------------- */
  window.PXUTheme.contentCreator.accordion = window.PXUTheme.contentCreator.accordion || {
    _bound: false,
    _handleClick: null,

    init() {
      if (this._bound) return;

      const containers = document.querySelectorAll('.accordion, [data-cc-accordion]');
      if (!containers.length) return;

      containers.forEach(container => {
        container.querySelectorAll('dd').forEach(el => el.setAttribute('aria-hidden', 'true'));
        container.querySelectorAll('dt > a').forEach(el => el.setAttribute('aria-expanded', 'false'));
      });

      this._handleClick = (e) => {
        const target = e.target.closest('.accordion > dt > a, [data-cc-accordion] > dt > a');
        if (!target) return;

        e.preventDefault();

        const isExpanded = target.getAttribute('aria-expanded') === 'true';
        const content = target.parentElement?.nextElementSibling;

        target.setAttribute('aria-expanded', String(!isExpanded));
        if (content) content.setAttribute('aria-hidden', String(isExpanded));
      };

      document.addEventListener('click', this._handleClick);
      this._bound = true;
    },

    unload() {
      if (!this._bound) return;
      document.removeEventListener('click', this._handleClick);
      this._handleClick = null;
      this._bound = false;
    }
  };

  /* -------------------------------------------
   * Main CPB module
   * ----------------------------------------- */
  const CONFIG = {
    DEBUG: false,
    RETRY_DELAYS_MS: [0, 80, 200, 450, 900],
    OOS_RETRY_DELAYS_MS: [150, 500, 1200]
  };

  const state = {
    builder: null,
    panelsContainer: null,

    customPricesContainer: null,
    freeAddonData: null,
    freeAddonTeaser: null,
    priceObserver: null,
    rafId: null,

    timeouts: new Set(),
    mountedId: null
  };

  const utils = {
    qs: (sel, parent = document) => parent.querySelector(sel),
    qsa: (sel, parent = document) => parent.querySelectorAll(sel),

    log(...args) {
      if (CONFIG.DEBUG) console.log('CPB:', ...args);
    },
    error(...args) {
      console.error('CPB:', ...args);
    },

    setTrackedTimeout(fn, delay) {
      const id = setTimeout(() => {
        state.timeouts.delete(id);
        fn();
      }, delay);
      state.timeouts.add(id);
      return id;
    },

    clearAllTimeouts() {
      state.timeouts.forEach(clearTimeout);
      state.timeouts.clear();
    },

    retry(fn, delays = CONFIG.RETRY_DELAYS_MS) {
      delays.forEach(delay => {
        utils.setTrackedTimeout(() => {
          try { fn(); } catch (_) {}
        }, delay);
      });
    },

    isVisible(el) {
      return !!(el && el.offsetParent !== null);
    },

    newMountId() {
      return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    }
  };

  function restoreZipWidget() {
    const widget = document.getElementById('shopify-zip-prod-widget');
    const originalHost = document.querySelector('[data-zip-original-host]');
    const cpbMount = document.querySelector('.cpb-zip-widget-mount');

    if (cpbMount) cpbMount.remove();

    if (widget && originalHost && widget.parentElement !== originalHost) {
      originalHost.appendChild(widget);
    }
  }

  function restoreAfterpayPlacement() {
    const placement = document.querySelector('[data-cpb-afterpay-placement]');
    const originalHost = document.querySelector('[data-afterpay-original-host]');
    const afterpayMount = document.querySelector('.cpb-afterpay-mount');

    if (afterpayMount) afterpayMount.remove();

    if (placement && originalHost && placement.parentElement !== originalHost) {
      originalHost.appendChild(placement);
      placement.style.display = '';
    }
  }

  function cleanup() {
    utils.clearAllTimeouts();

    if (state.rafId) {
      cancelAnimationFrame(state.rafId);
      state.rafId = null;
    }

    if (state.priceObserver) {
      state.priceObserver.disconnect();
      state.priceObserver = null;
    }

    window.PXUTheme.contentCreator.accordion?.unload?.();
    restoreZipWidget();
    restoreAfterpayPlacement();

    state.builder = null;
    state.panelsContainer = null;
    state.customPricesContainer = null;
    state.freeAddonData = null;
    state.freeAddonTeaser = null;
    state.mountedId = null;
  }

  function initGlobals() {
    state.builder = utils.qs('#product-builder');
    if (!state.builder) return false;

    state.panelsContainer = utils.qs('.cpb-panels-container', state.builder);
    return !!state.panelsContainer;
  }

  /* -------------------------------------------
   * Price formatting (commas, NO decimals)
   * ----------------------------------------- */
  const PRICE_REGEX = /(\d[\d,]*)/;
  const PRICE_DECIMAL_REGEX = /(\d[\d,]*(?:[.,]\d{1,2})?)/;

  function formatPrice(text) {
    const raw = (text || '').trim();
    const match = raw.match(PRICE_REGEX);
    if (!match) return '$0';

    const wholeNumber = match[1].replace(/,/g, '');
    const withCommas = wholeNumber.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    return `$${withCommas}`;
  }

  function parseAmount(text) {
    const raw = (text || '').trim();
    const match = raw.match(PRICE_DECIMAL_REGEX);
    if (!match) return null;

    const token = match[1];
    const hasDot = token.includes('.');
    const hasComma = token.includes(',');
    let normalized = token;

    if (hasDot && hasComma) {
      const decimalSeparator = token.lastIndexOf('.') > token.lastIndexOf(',') ? '.' : ',';
      if (decimalSeparator === '.') {
        normalized = token.replace(/,/g, '');
      } else {
        normalized = token.replace(/\./g, '').replace(',', '.');
      }
    } else if (hasComma && /,\d{1,2}$/.test(token)) {
      normalized = token.replace(/\./g, '').replace(',', '.');
    } else {
      normalized = token.replace(/,/g, '');
    }

    const amount = Number(normalized);
    return Number.isFinite(amount) ? amount : null;
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function formatMoneyCents(cents) {
    const amount = Number(cents) / 100;
    if (!Number.isFinite(amount) || amount <= 0) return '';

    const hasDecimals = !Number.isInteger(amount);
    return `$${amount.toLocaleString('en-AU', {
      minimumFractionDigits: hasDecimals ? 2 : 0,
      maximumFractionDigits: 2
    })}`;
  }

  function getFreeAddonData() {
    const node = document.getElementById('gp-frame-free-addon-data');
    if (!node) return null;

    try {
      const parsed = JSON.parse(node.textContent || '{}');
      const title = String(parsed.title || '').trim();
      const image = String(parsed.image || '').trim();
      const compareAtCents = Number(parsed.compareAtCents || 0);

      if (!title && !image && compareAtCents <= 0) return null;

      return { title, image, compareAtCents };
    } catch (error) {
      utils.error('Unable to parse free add-on data', error);
      return null;
    }
  }

  function buildFreeAddonMarkup(data) {
    const title = escapeHtml(data?.title || '');
    const image = escapeHtml(data?.image || '');
    const compareAtText = data?.compareAtCents > 0
      ? `Valued at ${formatMoneyCents(data.compareAtCents)}`
      : '';

    if (!title && !image && !compareAtText) return '';

    return `
      ${image ? `
        <div class="gp-frame-free-addon-offer__media">
          <img src="${image}" alt="${title || 'Free add-on'}" loading="eager" width="72" height="72">
        </div>
      ` : ''}
      <div class="gp-frame-free-addon-offer__content">
        <h3 class="gp-frame-free-addon-offer__title">${title ? `INCLUDES FREE ${title}` : 'Free add-on included'}</h3>
        ${compareAtText ? `<div class="gp-frame-free-addon-offer__compare">${compareAtText}</div>` : ''}
      </div>
    `;
  }

  function ensureFreeAddonTeaser() {
    if (!state.freeAddonData) {
      state.freeAddonData = getFreeAddonData();
    }

    if (!state.freeAddonData) return false;

    const markup = buildFreeAddonMarkup(state.freeAddonData);
    if (!markup) return false;

    if (!state.freeAddonTeaser) {
      state.freeAddonTeaser = document.createElement('div');
      state.freeAddonTeaser.className = 'gp-frame-free-addon-teaser gp-frame-free-addon-offer';
      state.freeAddonTeaser.setAttribute('data-frame-free-addon-teaser', '');
    }

    state.freeAddonTeaser.innerHTML = markup;
    return true;
  }

  function syncAfterpayAmount(priceText) {
    const placement = document.querySelector('[data-cpb-afterpay-placement]');
    if (!placement) return;

    const amount = parseAmount(priceText);
    if (amount === null) return;

    placement.setAttribute('data-amount', amount.toFixed(2));
  }

  function findBestPriceEl() {
    const prices = Array.from(utils.qsa('.cpb-product-price', state.panelsContainer));
    if (!prices.length) return null;

    const visible = prices.slice().reverse().find(utils.isVisible);
    return visible || prices[prices.length - 1];
  }

  function ensureCustomPriceContainer() {
    if (state.customPricesContainer) {
      ensureFreeAddonTeaser();
      return true;
    }

    const realPrice = findBestPriceEl();
    if (!realPrice) return false;

    const container = document.createElement('div');
    container.className = 'cpb-custom-prices-container';

    const priceWrapper = document.createElement('div');
    priceWrapper.className = 'cpb-custom-product-price-container';
    priceWrapper.style.display = 'block';
    priceWrapper.style.width = '100%';

    const customPrice = document.createElement('span');
    customPrice.className = 'cpb-custom-product-price';
    customPrice.textContent = formatPrice(realPrice.textContent);

    priceWrapper.appendChild(customPrice);
    container.appendChild(priceWrapper);

    state.customPricesContainer = container;
    state.builder.dataset.customProductPrice = 'true';
    ensureFreeAddonTeaser();

    const updatePrice = () => {
      if (state.rafId) return;
      state.rafId = requestAnimationFrame(() => {
        state.rafId = null;
        const currentPriceEl = findBestPriceEl() || realPrice;
        const currentText = currentPriceEl.textContent;
        customPrice.textContent = formatPrice(currentText);
        syncAfterpayAmount(currentText);
      });
    };

    state.priceObserver = new MutationObserver(updatePrice);
    state.priceObserver.observe(realPrice, {
      childList: true,
      characterData: true,
      subtree: true
    });

    return true;
  }

  function placeAfterpayWidget(mountId) {
    if (state.mountedId !== mountId) return false;

    const priceWrapper = document.querySelector('.cpb-custom-product-price-container');
    if (!priceWrapper) return false;

    let afterpayMount = document.querySelector('.cpb-afterpay-mount');
    if (!afterpayMount) {
      afterpayMount = document.createElement('div');
      afterpayMount.className = 'cpb-afterpay-mount';
      afterpayMount.style.display = 'block';
      afterpayMount.style.width = '100%';
      afterpayMount.style.marginTop = '8px';
      afterpayMount.style.flexBasis = '100%';
      afterpayMount.style.whiteSpace = 'normal';
      priceWrapper.insertAdjacentElement('afterend', afterpayMount);
    }

    const placement = document.querySelector('[data-cpb-afterpay-placement]');
    if (!placement) return false;

    if (placement.parentElement !== afterpayMount) {
      afterpayMount.appendChild(placement);
    }

    placement.style.display = 'block';

    const currentPriceEl = findBestPriceEl();
    if (currentPriceEl) syncAfterpayAmount(currentPriceEl.textContent);

    return true;
  }

  function moveDescriptionOnce(mountId) {
    const description = utils.qs('.product-block--description');
    const cpbDescription = utils.qs('.cpb-product-description');

    if (!description || !cpbDescription) return false;

    const move = () => {
      if (state.mountedId !== mountId) return;
      cpbDescription.replaceChildren(description);
    };

    const personalizeButton = utils.qs('.personalize-it--js');
    if (personalizeButton) {
      personalizeButton.addEventListener('click', move, { once: true });
    } else {
      move();
    }

    return true;
  }

  function placeElements(mountId) {
    if (state.mountedId !== mountId) return false;

    const productTitle = utils.qs('.cpb-product-title', state.panelsContainer);
    if (!productTitle) return false;

    const ratingStars = utils.qs('.product__information .klaviyo-star-rating-widget');

    ensureCustomPriceContainer();
    ensureFreeAddonTeaser();

    if (ratingStars && ratingStars.parentElement !== productTitle.parentElement) {
      productTitle.insertAdjacentElement('afterend', ratingStars);
    }

    if (state.customPricesContainer && !state.customPricesContainer.isConnected) {
      const target = ratingStars || productTitle;
      target.insertAdjacentElement('afterend', state.customPricesContainer);
    }

    if (state.freeAddonTeaser) {
      const zipMount = document.querySelector('.cpb-zip-widget-mount');
      const afterpayMount = document.querySelector('.cpb-afterpay-mount');
      const anchor = zipMount || afterpayMount || state.customPricesContainer || ratingStars || productTitle;

      if (anchor && state.freeAddonTeaser.previousElementSibling !== anchor) {
        anchor.insertAdjacentElement('afterend', state.freeAddonTeaser);
      }
    }

    return true;
  }

  function placeZipWidget(mountId) {
    if (state.mountedId !== mountId) return false;

    const priceWrapper = document.querySelector('.cpb-custom-product-price-container');
    if (!priceWrapper) return false;

    priceWrapper.style.display = 'block';
    priceWrapper.style.width = '100%';

    let zipMount = document.querySelector('.cpb-zip-widget-mount');
    if (!zipMount) {
      zipMount = document.createElement('div');
      zipMount.className = 'cpb-zip-widget-mount';
      zipMount.style.display = 'block';
      zipMount.style.width = '100%';
      zipMount.style.marginTop = '8px';
      zipMount.style.flexBasis = '100%';
      zipMount.style.whiteSpace = 'normal';
      const afterpayMount = document.querySelector('.cpb-afterpay-mount');
      if (afterpayMount) {
        afterpayMount.insertAdjacentElement('afterend', zipMount);
      } else {
        priceWrapper.insertAdjacentElement('afterend', zipMount);
      }
    }

    const widget = document.getElementById('shopify-zip-prod-widget');
    if (!widget) return false;

    if (widget.parentElement !== zipMount) {
      zipMount.appendChild(widget);
    }

    try {
      if (window.Zip?.init) window.Zip.init();
      if (window.zmWidget?.init) window.zmWidget.init();
    } catch (_) {}

    if (state.freeAddonTeaser) {
      zipMount.insertAdjacentElement('afterend', state.freeAddonTeaser);
    }

    return true;
  }

  function syncDynamicBuilderElements() {
    if (!state.mountedId) return;

    ensureCustomPriceContainer();
    ensureFreeAddonTeaser();
    utils.retry(() => placeElements(state.mountedId));
    utils.retry(() => placeAfterpayWidget(state.mountedId));
    utils.retry(() => placeZipWidget(state.mountedId));
  }

  const NAV_REGEX = /PREVIOUS|NEXT|RESET|PREVIOUS TAB|NEXT TAB/i;

  function removeNavigationButtons() {
    const buttons = utils.qsa('.cpb-product-actions button, .cpb-product-actions .cpb-actions-btn');
    if (!buttons.length) return false;

    let removed = 0;
    buttons.forEach(btn => {
      if (NAV_REGEX.test((btn.textContent || '').trim())) {
        btn.remove();
        removed++;
      }
    });

    return removed > 0;
  }

  function updateOutOfStockOverlays() {
    const wrappers = utils.qsa('.out-stock-wrapper');
    if (!wrappers.length) return false;

    wrappers.forEach(wrapper => {
      const overlay = utils.qs('.out-stock-overlay span', wrapper);
      const img = utils.qs('img', wrapper);
      if (!overlay || !img) return;

      const title = (img.alt || 'Product')
        .replace(/\s*\([^)]*\)\s*$/g, '')
        .trim();

      overlay.textContent = `${title} - OUT OF STOCK`;
    });

    return true;
  }

  function init() {
    cleanup();
    state.mountedId = utils.newMountId();
    const mountId = state.mountedId;

    if (!initGlobals()) {
      utils.error('Product builder not found');
      return;
    }

    window.PXUTheme.contentCreator.accordion?.init?.();

    state.freeAddonData = getFreeAddonData();
    ensureCustomPriceContainer();

    utils.retry(() => moveDescriptionOnce(mountId));
    utils.retry(() => placeElements(mountId));
    utils.retry(() => placeAfterpayWidget(mountId));

    // Zip widget retries
    utils.retry(() => placeZipWidget(mountId));
    utils.setTrackedTimeout(() => placeZipWidget(mountId), 1200);
    utils.setTrackedTimeout(() => placeZipWidget(mountId), 2500);

    utils.retry(removeNavigationButtons);

    CONFIG.OOS_RETRY_DELAYS_MS.forEach(delay => {
      utils.setTrackedTimeout(() => updateOutOfStockOverlays(), delay);
    });
  }

  document.body.addEventListener('CPB_ON_PRODUCT_MOUNTED', init);
  document.body.addEventListener('CPB_ON_OPTIONS_UPDATE', syncDynamicBuilderElements);
  window.addEventListener('pagehide', cleanup);
  window.addEventListener('beforeunload', cleanup);
})();
